import React, { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ProjectManagementService } from "@application/useCases/workspace/ProjectManagementService";
import { UserDirectoryService } from "@application/useCases/workspace/UserDirectoryService";
import { UserPicker } from "@presentation/components/team/UserPicker";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { describeManagerInvariantDenial } from "@presentation/common/utils/managerInvariantDenial";
import { describeProjectRolePermissions } from "@presentation/common/utils/roleDescriptions";
import { PROJECT_ROLE_NAMES, ProjectRoles } from "@domain/schemas/workspace";
import type { ProjectDetail, ProjectRoleName, UserSummary } from "@domain/schemas/workspace";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface AssignProjectRoleDialogProps {
  projectId: string;
  projectName: string;
  /**
  Present for "change role" (user field locked); absent for "add member" (empty, editable user field).
  */
  existingMember?: { userId: string; role: ProjectRoleName };
  /**
  Current project members' user ids, so "add member" search results exclude people already on the
  project. Omitted by callers with no membership list to draw from (e.g. a team's project inventory,
  which deliberately exposes no existing-member list - design.md).
  */
  existingMemberUserIds?: readonly string[];
  onClose: () => void;
  onSuccess: (updated: ProjectDetail) => void;
}

/**
 * One dialog for both add-member and change-role, mirroring `AssignTeamRoleDialog` - there is no
 * "member with no role", so the two are the same backend call, distinguished only by whether the
 * user field is pre-filled and locked. Reachable from both a team's project inventory (add-only,
 * since that surface deliberately exposes no existing-member list) and the project's own
 * management page (both add and change-role).
 *
 * The user field is a searchable picker backed by `GET /users?search=` (SF-2783); for "change role"
 * it is locked and its display text is resolved via `GET /users?ids=` rather than showing the raw id.
 */
export const AssignProjectRoleDialog: React.FC<AssignProjectRoleDialogProps> = ({ projectId, projectName, existingMember, existingMemberUserIds, onClose, onSuccess }) => {
  const projectManagementService = useInjection(ProjectManagementService);
  const notifySuccess = useWorkspaceSuccessNotification();
  const userDirectoryService = useInjection(UserDirectoryService);
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(existingMember ? { id: existingMember.userId, username: existingMember.userId, displayName: existingMember.userId } : null);
  const [role, setRole] = useState<ProjectRoleName>(existingMember?.role ?? ProjectRoles.Reader);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isChangeRole = Boolean(existingMember);
  const canSubmit = Boolean(selectedUser) && !isSubmitting;

  useEffect(() => {
    if (!existingMember) return;
    let isCancelled = false;
    void userDirectoryService.getUsersByIdsAsync([existingMember.userId]).then((users) => {
      if (isCancelled || users.length === 0) return;
      setSelectedUser(users[0]);
    });
    return () => {
      isCancelled = true;
    };
  }, [existingMember?.userId]);

  const handleSubmit = async () => {
    if (!selectedUser) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const updated = await projectManagementService.assignProjectRoleAsync(projectId, selectedUser.id, role);
      notifySuccess("project", isChangeRole ? "roleChanged" : "memberAdded");
      onSuccess(updated);
      onClose();
    } catch (error) {
      setErrorMessage(describeManagerInvariantDenial(error, "project") ?? extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not assign the role.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title={isChangeRole ? `Change role in ${projectName}` : `Add member to ${projectName}`} onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <label htmlFor="assign-project-role-user-id" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            User
          </label>
          <UserPicker id="assign-project-role-user-id" value={selectedUser} onChange={setSelectedUser} disabled={isChangeRole} excludeUserIds={existingMemberUserIds} />
        </div>
        <div>
          <label htmlFor="assign-project-role-role" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Role
          </label>
          <DropDownList
            id="assign-project-role-role"
            style={{ width: "100%" }}
            data={[...PROJECT_ROLE_NAMES]}
            value={role}
            onChange={(event) => setRole(event.value as ProjectRoleName)}
            valueRender={(element, value) => (value ? React.cloneElement(element, { title: describeProjectRolePermissions(value) }) : element)}
            itemRender={(li, itemProperties) => React.cloneElement(li, { title: describeProjectRolePermissions(itemProperties.dataItem as ProjectRoleName) })}
          />
        </div>
        {errorMessage && <div style={{ color: "#7f1d1d" }}>{errorMessage}</div>}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={!canSubmit} onClick={handleSubmit}>
          {isChangeRole ? "Save role" : "Add member"}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
